import { AxiosError } from 'axios'
import { logNetworkError } from 'services/Sentry'
import { Mutex } from 'async-mutex' // Import the Mutex class
import {
  refresh,
  eraseTokens,
  getAccessToken,
  isTokenExpiringWithin,
} from 'services/Authentication'
import { ROUTES } from 'Constants'
import { ConfigureGlobalService, ConfigureClinicService } from 'back-end-api'

export default function InitBackEndServices() {
  const goBackToLogin = () => {
    eraseTokens()

    const next =
      window.location.pathname !== ROUTES.LOGIN(undefined) ? window.location.pathname : undefined

    // Use a hard navigation rather than react-router's history: these callbacks
    // are registered once with the back-end-api client and fire asynchronously
    // from axios, outside React's render/Router context, where a captured
    // `history` object is undefined (caused "Cannot read properties of undefined
    // (reading 'push')" and a broken logout).
    window.location.href = ROUTES.LOGIN(next)
  }

  const onError = async (err: AxiosError) => {
    if (err.response?.status === 401) {
      // Distinguish an authentication 401 (expired/invalid token) from a
      // business-logic 401 (not authorized for this resource). Only the former
      // should ever lead to logout.
      const errorData = err.response?.data as { message?: string } | undefined
      const message = errorData?.message?.toLowerCase()
      const isAuthError =
        !errorData ||
        Boolean(message?.includes('token')) ||
        Boolean(message?.includes('unauthorized')) ||
        Boolean(message?.includes('authentication'))

      if (!isAuthError) {
        logNetworkError(err)
        return
      }

      // Don't log out immediately: a 401 can be an in-flight request during a
      // token refresh or a transient hiccup (e.g. during AV calls). Try to
      // refresh first; only log out if the refresh definitively fails.
      try {
        const refreshedToken = await refresh()
        if (!refreshedToken) {
          goBackToLogin()
        }
      } catch {
        // Network error during refresh - treat as transient, do not log out.
      }
      return
    }

    logNetworkError(err)
  }

  const mutex = new Mutex()

  const getToken = async (): Promise<string> => {
    const accessToken = getAccessToken()
    if (accessToken && !isTokenExpiringWithin(accessToken, 1000 * 60)) {
      return accessToken
    }

    const refreshedToken = await mutex.runExclusive(async () => {
      const latestAccessToken = getAccessToken()
      if (latestAccessToken !== accessToken) {
        return latestAccessToken
      }

      return refresh()
    })

    if (refreshedToken) {
      return refreshedToken
    }

    // Refresh returned nothing but we may still hold an access token (e.g. the
    // refresh failed on a transient network error). Prefer retrying with the
    // existing token over logging out; the server will return 401 if it is
    // truly expired, and onError will handle it.
    const currentToken = getAccessToken()
    if (currentToken) {
      return currentToken
    }

    goBackToLogin()

    return ''
  }

  const globalServiceUrl = process.env.REACT_APP_GLOBAL_SERVICE_URL
  if (!globalServiceUrl) {
    throw new Error('REACT_APP_GLOBAL_SERVICE_URL environment variable is not set')
  }

  const clinicServiceUrl = process.env.REACT_APP_CLINIC_SERVICE_URL
  if (!clinicServiceUrl) {
    throw new Error('REACT_APP_CLINIC_SERVICE_URL environment variable is not set')
  }

  ConfigureGlobalService({
    baseUrl: globalServiceUrl,
    onErrorCallback: onError,
    getTokenCallback: getToken,
  })

  ConfigureClinicService({
    baseUrl: clinicServiceUrl,
    onErrorCallback: onError,
    getTokenCallback: getToken,
  })
}
